import { getCandidateLabels } from "components/identifyEventsTool/tool/ScaleWeightsChart.utils";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { EventCreate } from "src/store/api/generatedApi";

dayjs.extend(utc);

export interface VisibleRangeInputs {
  eventStart: Dayjs;
  eventEnd: Dayjs;
  pairedEventStart?: Dayjs | null;
  pairedEventEnd?: Dayjs | null;
  massEventStart?: Dayjs | null;
  massEventEnd?: Dayjs | null;
  measurementStart?: Dayjs | null;
  measurementEnd?: Dayjs | null;
  previousDropBound?: Dayjs | null;
  nextDropBound?: Dayjs | null;
  paddingSeconds: number;
}

const earliestOf = (candidates: Dayjs[]): Dayjs =>
  candidates.reduce((earliest, current) =>
    current.isBefore(earliest) ? current : earliest
  );

const latestOf = (candidates: Dayjs[]): Dayjs =>
  candidates.reduce((latest, current) =>
    current.isAfter(latest) ? current : latest
  );

export const computeVisibleStart = ({
  eventStart,
  pairedEventStart,
  massEventStart,
  measurementStart,
  previousDropBound,
  paddingSeconds,
}: VisibleRangeInputs): Dayjs => {
  const paddedEventStart = eventStart.subtract(paddingSeconds, "second");
  const candidates: Dayjs[] = [paddedEventStart];
  if (pairedEventStart != null) candidates.push(pairedEventStart);
  if (massEventStart != null) candidates.push(massEventStart);
  if (measurementStart?.isBefore(paddedEventStart)) {
    candidates.push(measurementStart.subtract(paddingSeconds, "second"));
  }
  if (previousDropBound != null) candidates.push(previousDropBound);
  return earliestOf(candidates);
};

export const computeVisibleEnd = ({
  eventEnd,
  pairedEventEnd,
  massEventEnd,
  measurementEnd,
  nextDropBound,
  paddingSeconds,
}: VisibleRangeInputs): Dayjs => {
  const paddedEventEnd = eventEnd.add(paddingSeconds, "second");
  const candidates: Dayjs[] = [paddedEventEnd];
  if (pairedEventEnd != null) candidates.push(pairedEventEnd);
  if (massEventEnd != null) candidates.push(massEventEnd);
  if (measurementEnd?.isAfter(paddedEventEnd)) {
    candidates.push(measurementEnd.add(paddingSeconds, "second"));
  }
  if (nextDropBound != null) candidates.push(nextDropBound);
  return latestOf(candidates);
};

/**
 * Returns the end of the nearest earlier detected drop and the start of the
 * nearest later detected drop on the same area.
 * A side with no detected drop within `maxReachSeconds` has no bound.
 */
export const findNeighbouringDropBounds = ({
  candidateEvents,
  area,
  eventStart,
  eventEnd,
  massEventStart,
  massEventEnd,
  maxReachSeconds,
}: {
  candidateEvents: EventCreate[];
  area: string | null;
  eventStart: Dayjs;
  eventEnd: Dayjs;
  massEventStart: Dayjs | null;
  massEventEnd: Dayjs | null;
  maxReachSeconds: number;
}): { previousDropBound: Dayjs | null; nextDropBound: Dayjs | null } => {
  const candidateWindows = candidateEvents
    .filter((candidate) => getCandidateLabels(candidate).area === area)
    .map((candidate) => ({
      start: dayjs.utc(candidate.event.start),
      end: dayjs.utc(candidate.event.end),
    }));
  // A neighbour can overlap the edge of the mass event, because detection
  // windows are padded.
  const isThisDrop = ({ start, end }: { start: Dayjs; end: Dayjs }): boolean =>
    (start.isBefore(eventEnd) && end.isAfter(eventStart)) ||
    (massEventStart != null &&
      massEventEnd != null &&
      !start.isBefore(massEventStart) &&
      !end.isAfter(massEventEnd));
  const neighbourWindows = candidateWindows.filter(
    (candidateWindow) => !isThisDrop(candidateWindow)
  );
  const reachStart = eventStart.subtract(maxReachSeconds, "second");
  const reachEnd = eventEnd.add(maxReachSeconds, "second");
  const earlierDropEnds = neighbourWindows
    .map(({ end }) => end)
    .filter((end) => !end.isAfter(eventStart) && !end.isBefore(reachStart));
  const laterDropStarts = neighbourWindows
    .map(({ start }) => start)
    .filter((start) => !start.isBefore(eventEnd) && !start.isAfter(reachEnd));
  return {
    previousDropBound:
      earlierDropEnds.length > 0 ? latestOf(earlierDropEnds) : null,
    nextDropBound:
      laterDropStarts.length > 0 ? earliestOf(laterDropStarts) : null,
  };
};

/** Scales the time shown either side of the drop. */
export const zoomAroundEvent = ({
  eventStart,
  eventEnd,
  defaultStart,
  defaultEnd,
  zoomFactor,
}: {
  eventStart: Dayjs;
  eventEnd: Dayjs;
  defaultStart: Dayjs;
  defaultEnd: Dayjs;
  zoomFactor: number;
}): { visibleStart: Dayjs; visibleEnd: Dayjs } => ({
  visibleStart: eventStart.subtract(
    Math.round(zoomFactor * eventStart.diff(defaultStart)),
    "millisecond"
  ),
  visibleEnd: eventEnd.add(
    Math.round(zoomFactor * defaultEnd.diff(eventEnd)),
    "millisecond"
  ),
});
